import { useEffect, useState, useRef } from "react";
import { IInputField, IInputOptions } from "./Input";
import { getInputData, initForm, asyncValidateInputs } from "../../../shared/utility";
import { useCreateInput } from "./useCreateInput";
import { EInputUpdateAction } from "../../../context/InputContext";

interface IParams<T> {
  data?: T;
  inputs: IInputField;
  setInputs: React.Dispatch<React.SetStateAction<IInputField>>;
  options?: IInputOptions;
  updateAction?: EInputUpdateAction;
  initDone?: boolean;
  disabled?: boolean;
  allowInvalid?: boolean;
}

export function useInputs<T>({
  data,
  inputs,
  setInputs,
  options = {},
  updateAction,
  initDone,
  disabled,
  allowInvalid,
}: IParams<T>) {
  const [showValidation, setShowValidation] = useState(false);
  const previousDataRef = useRef<T | undefined>(undefined);

  useEffect(() => {
    if (!initDone && data) {
      if (previousDataRef.current) {
        const diff = {} as Partial<T>;
        let hasChanges = false;
        for (const key in data) {
          if ((data as any)[key] !== (previousDataRef.current as any)[key]) {
            (diff as any)[key] = (data as any)[key];
            hasChanges = true;
          }
        }
        if (hasChanges) {
          initForm(setInputs, diff);
        }
      } else {
        initForm(setInputs, data);
      }
      previousDataRef.current = data;
    }
  }, [data, setInputs, initDone]);

  const createInput = useCreateInput(inputs, setInputs, {
    updateAction,
    showValidation,
    data: updateAction ? (data as any) ?? undefined : undefined,
    disabled,
    ...options,
  });

  const submit = async () => {
    const isValid = await asyncValidateInputs(setInputs);
    if (isValid || allowInvalid) {
      return getInputData<T>(inputs);
    }
    setShowValidation(true);
    return null;
  };

  return { createInput, submit, inputs, setInputs };
}
